<template>
  <div>
    <div class="row mb-3">
      <div class="col-md-4">
        <select class="form-select" v-model="statusFilter">
          <option value="">Todos los estados</option>
          <option value="RESUELTO">Resuelto</option>
          <option value="CANCELADO">Cancelado</option>
        </select>
      </div>
      <div class="col-md-4">
        <select class="form-select" v-model="actionFilter">
          <option value="">Todos los tipos</option>
          <option value="COMPROBANTE">Comprobante</option>
          <option value="CERTIFICADO">Certificado</option>
          <option value="CONTRASEÑA">Restablecer Contraseña</option>
        </select>
      </div>
      <div class="col-md-4">
        <input
          type="text"
          class="form-control"
          v-model="searchText"
          placeholder="Buscar por documento..."
        />
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th class="text-center">Nro</th>
                <th class="text-center">Tipo</th>
                <th class="text-center">Atendido por</th>
                <th class="text-center">Estado</th>
                <th class="text-center">Fecha Conclusión</th>
                <th class="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="7" class="text-center">
                  <i class="fa fa-spinner fa-spin"></i> Cargando...
                </td>
              </tr>
              <tr v-else-if="paginatedItems.length === 0">
                <td colspan="7" class="text-center">
                  <EmptyState
                    title="No hay tickets concluidos."
                    :size="450"
                    fontSize="fs-5"
                  />
                </td>
              </tr>
              <tr
                v-else
                v-for="(ticket, index) in paginatedItems"
                :key="ticket.id"
              >
                <td class="text-center">
                  {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                </td>
                <td class="text-center">
                  <span class="badge" :class="actionBadgeClass(ticket.action)">
                    {{ actionText(ticket.action) }}
                  </span>
                </td>
                <td class="text-center">{{ ticket.user?.name || "N/A" }}</td>
                <td class="text-center">
                  <span class="badge" :class="statusBadgeClass(ticket.status)">
                    {{ statusText(ticket.status) }}
                  </span>
                </td>
                <td class="text-center">{{ formatDate(ticket.updated_at) }}</td>
                <td class="text-center">
                  <div class="btn-group" role="group">
                    <button
                      type="button"
                      class="btn btn-sm btn-info"
                      @click="viewDetail(ticket)"
                      title="Ver detalle"
                    >
                      <i class="fa fa-eye"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <Pagination
          v-if="filteredList.length > itemsPerPage"
          :current-page="currentPage"
          :total-items="filteredList.length"
          :items-per-page="itemsPerPage"
          @page-changed="handlePageChange"
        />
      </div>
    </div>

    <DetailModal ref="detailModal" @ticket-updated="onTicketUpdated" />
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import Pagination from "@/pages/components/Pagination.vue";
import DetailModal from "./DetailModal.vue";
import EmptyState from "@/components/emptyState.vue";

const props = defineProps({
  tickets: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["ticket-updated"]);

const detailModal = ref(null);
const statusFilter = ref("");
const actionFilter = ref("");
const searchText = ref("");
const currentPage = ref(1);
const itemsPerPage = ref(10);

const filteredList = computed(() => {
  let filtered = [...props.tickets];

  if (statusFilter.value) {
    filtered = filtered.filter(
      (ticket) => ticket.status === statusFilter.value
    );
  }

  if (actionFilter.value) {
    filtered = filtered.filter(
      (ticket) => ticket.action === actionFilter.value
    );
  }

  if (searchText.value) {
    const search = searchText.value.toLowerCase();
    filtered = filtered.filter(
      (ticket) =>
        ticket.participant_document.toLowerCase().includes(search) ||
        ticket.event?.title.toLowerCase().includes(search) ||
        ticket.detail.toLowerCase().includes(search)
    );
  }

  return filtered;
});

const paginatedItems = computed(() => {
  const startIndex = (currentPage.value - 1) * itemsPerPage.value;
  const endIndex = startIndex + itemsPerPage.value;
  return filteredList.value.slice(startIndex, endIndex);
});

watch([statusFilter, actionFilter, searchText], () => {
  currentPage.value = 1;
});

function handlePageChange(page) {
  currentPage.value = page;
}

function viewDetail(ticket) {
  detailModal.value.show(ticket);
}

function onTicketUpdated(updatedTicket) {
  emit("ticket-updated", updatedTicket);
}

function actionText(action) {
  const texts = {
    COMPROBANTE: "Comprobante",
    CERTIFICADO: "Certificado",
    CONTRASEÑA: "Contraseña",
  };
  return texts[action] || action;
}

function actionBadgeClass(action) {
  const classes = {
    COMPROBANTE: "bg-primary",
    CERTIFICADO: "bg-success",
    CONTRASEÑA: "bg-warning",
  };
  return classes[action] || "bg-secondary";
}

function statusText(status) {
  const texts = {
    RESUELTO: "Resuelto",
    CANCELADO: "Cancelado",
  };
  return texts[status] || status;
}

function statusBadgeClass(status) {
  const classes = {
    RESUELTO: "bg-success",
    CANCELADO: "bg-danger",
  };
  return classes[status] || "bg-secondary";
}

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("es-ES", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
</script>
